import { AgCharts, ModuleRegistry, AllCommunityModule } from 'ag-charts-community';

// Single entry point for standalone ag-charts usage.
//
// Licensing: this is `ag-charts-community` (MIT), NOT `ag-charts-enterprise` (Commercial). The app
// does already load the enterprise package — main.js registers `AgChartsEnterpriseModule` into
// ag-grid's `IntegratedChartsModule` — but that entitlement covers charts drawn *by the grid*, not
// standalone charts drawn by our own components. Standalone charts therefore use the community API,
// which needs no additional licence.
//
// Registry: ag-charts 13 moved to the same opt-in module registry as ag-grid 33, and both the
// community and enterprise packages resolve to a single hoisted `ag-charts-core` — so there is one
// shared registry, not one per package. Blindly registering the community bundle into it could
// replace enterprise series with their community equivalents and silently degrade the grid's
// integrated charts. Hence: only populate the registry when it is still empty, and let whichever
// side initialises second leave the other's entries alone.

// Shared look for every standalone chart: transparent background (the card underneath already
// carries the surface colour), Roboto to match the rest of the page (Vuetify's default typeface,
// which ag-charts does not inherit from the DOM since it renders to a canvas), and round legend
// markers to match the dot used everywhere else a category gets a colour swatch (see
// .category-breakdown__swatch in orderTracking).
const CHART_FONT_FAMILY = 'Roboto, sans-serif';

export const CHART_THEME = {
	overrides: {
		common: {
			background: { fill: 'transparent' },
			title: { fontFamily: CHART_FONT_FAMILY },
			subtitle: { fontFamily: CHART_FONT_FAMILY },
			legend: { item: { marker: { shape: 'circle' }, label: { fontFamily: CHART_FONT_FAMILY } } },
			axes: {
				category: { label: { fontFamily: CHART_FONT_FAMILY } },
				number: { label: { fontFamily: CHART_FONT_FAMILY } },
			},
		},
		bar: { series: { cornerRadius: 6 } },
	},
};

// `common` only covers the AgBaseThemeableChartOptions surface (background/title/legend/axes) - a
// donut's centre innerLabels are series-specific and need their own fontFamily (see
// buildDistributionOptions below), the theme has no slot for them.
export { CHART_FONT_FAMILY };

let registered = false;

function isRegistryEmpty() {
	for (const _module of ModuleRegistry.listModules()) return false;
	return true;
}

export function ensureAgChartsRegistered() {
	if (registered) return;
	if (isRegistryEmpty()) ModuleRegistry.registerModules([AllCommunityModule]);
	registered = true;
}

export { AgCharts };
